import { useState, useEffect, useRef, useCallback } from 'react';
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getNotifications, getUnreadCount, markAsRead, markAllAsRead, Notification, resolveNotificationPath } from '../../services/notificationService';
import './NotificationBell.css';

import { NotificationsNone } from '@mui/icons-material';
interface NotificationBellProps {
    className?: string;
}

const NotificationBell: React.FC<NotificationBellProps> = ({ className }) => {
    const { t, i18n } = useTranslation('common');
    const timeNs = 'notificationBell.timeAgo';
    const navigate = useNavigate();
    const [isOpen, setIsOpen] = useState(false);
    const [notifications, setNotifications] = useState<Notification[]>([]);
    const [unreadCount, setUnreadCount] = useState(0);
    const [isLoading, setIsLoading] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);
    const bellRef = useRef<HTMLButtonElement>(null);
    const isOpenRef = useRef(isOpen);
    isOpenRef.current = isOpen;

    // Fetch notifications and unread count
    const fetchNotifications = useCallback(async () => {
        try {
            setIsLoading(true);
            const data = await getNotifications(10, 0, false);
            setNotifications(data.notifications);
        } catch (error) {
            console.error('Failed to fetch notifications:', error);
        } finally {
            setIsLoading(false);
        }
    }, []);

    const fetchUnreadCount = useCallback(async () => {
        try {
            const count = await getUnreadCount();
            setUnreadCount(count);
        } catch (error) {
            console.error('Failed to fetch unread count:', error);
        }
    }, []);

    // Montage unique : ne pas dépendre de isOpen (sinon chaque ouverture/fermeture relance
    // des GET et recrée l’intervalle — risque de rafales si le layout re-render souvent).
    useEffect(() => {
        fetchNotifications();
        fetchUnreadCount();

        const interval = setInterval(() => {
            void fetchUnreadCount();
            if (isOpenRef.current) {
                void fetchNotifications();
            }
        }, 30000);

        return () => clearInterval(interval);
    }, [fetchNotifications, fetchUnreadCount]);

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (
                dropdownRef.current &&
                !dropdownRef.current.contains(event.target as Node) &&
                bellRef.current &&
                !bellRef.current.contains(event.target as Node)
            ) {
                setIsOpen(false);
            }
        };

        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isOpen]);

    const toggleDropdown = () => {
        setIsOpen(!isOpen);
        if (!isOpen) {
            fetchNotifications();
        }
    };

    const handleNotificationClick = async (notification: Notification) => {
        // Mark as read if unread
        if (!notification.isRead) {
            try {
                await markAsRead(notification.id);
                setUnreadCount(prev => Math.max(0, prev - 1));
                setNotifications(prev =>
                    prev.map(n =>
                        n.id === notification.id ? { ...n, isRead: true } : n
                    )
                );
            } catch (error) {
                console.error('Failed to mark notification as read:', error);
            }
        }

        const targetPath = resolveNotificationPath(notification);
        if (targetPath) {
            navigate(targetPath);
        }

        setIsOpen(false);
    };

    const handleMarkAllAsRead = async () => {
        try {
            await markAllAsRead();
            setUnreadCount(0);
            setNotifications(prev =>
                prev.map(n => ({ ...n, isRead: true }))
            );
        } catch (error) {
            console.error('Failed to mark all as read:', error);
        }
    };

    const getNotificationIcon = (type: string, title?: string): string => {
        // Special case for event registration requests (private events)
        if (type === 'event_registration' && title === 'Demande d\'inscription') {
            return '🙋'; // Raising hand for requests
        }

        const iconMap: { [key: string]: string } = {
            'new_booking': '📅',
            'booking_confirmed': '✅',
            'booking_cancelled': '❌',
            'reschedule_request': '🔄',
            'forfait_purchased': '🎁',
            'forfait_expiring': '⏰',
            'event_registration': '🎟️',
            'new_message': '💬',
            'cart_checkout_summary': '🛒',
            'quote_accepted': '📄',
            'quote_rejected': '📄',
        };
        return iconMap[type] || '🔔';
    };

    const formatTimeAgo = useCallback((dateString: string): string => {
        const date = new Date(dateString);
        const now = new Date();
        const seconds = Math.floor((now.getTime() - date.getTime()) / 1000);

        if (seconds < 60) return t(`${timeNs}.justNow`);
        if (seconds < 3600) return t(`${timeNs}.minutesAgo`, { count: Math.floor(seconds / 60) });
        if (seconds < 86400) return t(`${timeNs}.hoursAgo`, { count: Math.floor(seconds / 3600) });
        if (seconds < 604800) return t(`${timeNs}.daysAgo`, { count: Math.floor(seconds / 86400) });
        const locale = (i18n.language || 'fr').split('-')[0];
        return date.toLocaleDateString(locale);
    }, [t, i18n.language]);

    return (
        <div className={`notification-bell-container ${className || ''}`}>
            <button
                ref={bellRef}
                className={`notification-bell ${isOpen ? 'active' : ''}`}
                onClick={toggleDropdown}
                aria-label={t('notificationBell.ariaLabel')}
            >
                <NotificationsNone className="bell-icon" />
                {unreadCount > 0 && (
                    <span className="notification-badge">
                        {unreadCount > 99 ? '99+' : unreadCount}
                    </span>
                )}
            </button>

            {isOpen && (
                <div ref={dropdownRef} className="notification-dropdown">
                    <div className="notification-header">
                        <h3>{t('notificationBell.header')}</h3>
                        {unreadCount > 0 && (
                            <button
                                className="mark-all-read-btn"
                                onClick={handleMarkAllAsRead}
                            >
                                {t('notificationBell.markAllRead')}
                            </button>
                        )}
                    </div>

                    <div className="notification-list">
                        {isLoading ? (
                            <div className="notification-loading">
                                <div className="loading-spinner"></div>
                                <p>{t('notificationBell.loading')}</p>
                            </div>
                        ) : notifications.length === 0 ? (
                            <div className="notification-empty">
                                <span className="empty-icon">🔔</span>
                                <p>{t('notificationBell.empty')}</p>
                            </div>
                        ) : (
                            notifications.map(notification => (
                                <div
                                    key={notification.id}
                                    className={`notification-item ${!notification.isRead ? 'unread' : ''}`}
                                    onClick={() => handleNotificationClick(notification)}
                                >
                                    <div className="notification-icon">
                                        {getNotificationIcon(notification.type, notification.title)}
                                    </div>
                                    <div className="notification-content">
                                        <div className="notification-title">{notification.title}</div>
                                        <div className="notification-message">{notification.message}</div>
                                        <div className="notification-time">
                                            {formatTimeAgo(notification.createdAt)}
                                        </div>
                                    </div>
                                    {!notification.isRead && <div className="unread-indicator"></div>}
                                </div>
                            ))
                        )}
                    </div>

                    {notifications.length > 0 && (
                        <div className="notification-footer">
                            <a href="/app/notifications" className="view-all-link">
                                {t('notificationBell.viewAll')}
                            </a>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default NotificationBell;
